import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload } from 'firebase/messaging';

// Suppress Firebase installation errors from crashing the app
// These happen when Firebase can't reach the installations API (e.g. localhost not in authorized domains)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event.reason?.message || event.reason || '');
    if (msg.includes('firebaseinstallations') || msg.includes('installations') || msg.includes('Firebase')) {
      event.preventDefault();
      console.warn('Suppressed Firebase error (push notifications disabled):', msg);
    }
  });
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;


export const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId
  );
};


export const initializeFirebase = (): FirebaseApp | null => {
  if (!isFirebaseConfigured()) {
    console.warn('Firebase is not configured. Push notifications will be disabled.');
    return null;
  }

  if (!firebaseApp && getApps().length === 0) {
    try {
      firebaseApp = initializeApp(firebaseConfig);
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      return null;
    }
  } else if (getApps().length > 0) {
    firebaseApp = getApps()[0];
  }

  return firebaseApp;
};

export const getFirebaseMessaging = (): Messaging | null => {
  if (!isFirebaseConfigured()) {
    return null;
  }

  if (!messaging) {
    const app = initializeFirebase();
    if (app) {
      try {
        messaging = getMessaging(app);
      } catch (error) {
        console.error('Failed to get Firebase Messaging:', error);
        return null;
      }
    }
  }

  return messaging;
};

/**
 * Request notification permission and get FCM token
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return null;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return null;
    }

    console.log('Notification permission granted');
    return await getFcmToken();
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};


/**
 * Wait for service worker to be ready/active
 */
const waitForServiceWorkerActive = async (registration: ServiceWorkerRegistration): Promise<ServiceWorker> => {
  // If already active, return immediately
  if (registration.active) {
    return registration.active;
  }

  // Wait for the service worker to become active
  return new Promise((resolve, reject) => {
    const sw = registration.installing || registration.waiting;

    if (!sw) {
      reject(new Error('No service worker found'));
      return;
    }

    const onStateChange = () => {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', onStateChange);
        resolve(sw);
      } else if (sw.state === 'redundant') {
        sw.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant'));
      }
    };

    sw.addEventListener('statechange', onStateChange);

    // Timeout after 10 seconds
    setTimeout(() => {
      sw.removeEventListener('statechange', onStateChange);
      reject(new Error('Service worker activation timeout'));
    }, 10000);
  });
};

export const getFcmToken = async (): Promise<string | null> => {
  try {
    const messagingInstance = getFirebaseMessaging();

    if (!messagingInstance) {
      console.warn('Firebase Messaging not initialized');
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service worker registered, waiting for activation...');

    // Wait for service worker to be active
    await waitForServiceWorkerActive(registration);
    console.log('Service worker is now active');

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

    if (!vapidKey) {
      console.warn('VAPID key not configured, FCM token will not be obtained');
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('FCM Token obtained successfully');
      console.log('FCM Token (first 50 chars):', token.substring(0, 50) + '...');
      console.log('FCM Token length:', token.length);
      return token;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

export const onForegroundMessage = (
  callback: (payload: MessagePayload) => void
): (() => void) | null => {
  const messagingInstance = getFirebaseMessaging();

  if (!messagingInstance) {
    console.warn('Firebase Messaging not initialized');
    return null;
  }

  return onMessage(messagingInstance, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};


export const showNotification = (title: string, options?: globalThis.NotificationOptions): void => {
  console.log('[Firebase] showNotification called:', { title, options });
  console.log('[Firebase] Notification permission:', Notification.permission);

  if (!('Notification' in window)) {
    console.warn('[Firebase] Notifications not supported in this browser');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[Firebase] Notification permission not granted:', Notification.permission);
    return;
  }

  try {
    const notification = new Notification(title, {
      body: options?.body,
      icon: '/logo192.png',
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    console.log('[Firebase] Notification shown successfully');
  } catch (error) {
    console.error('[Firebase] Failed to show notification:', error);
  }
};

export default {
  isFirebaseConfigured,
  initializeFirebase,
  getFirebaseMessaging,
  requestNotificationPermission,
  getFcmToken,
  onForegroundMessage,
  showNotification,
};
